/**
 * The YouTube-widget and Vimeo-player.js wire protocols, as pure codecs.
 *
 * The adapters in `packages/ui` own the browser side (the `message` listener,
 * the origin check, the timers); this file owns what goes ON the wire and what
 * comes off it. That split is what makes the protocol testable at all — there
 * is no DOM harness in this repo, and `packages/shared` has no DOM lib.
 *
 * Two rules hold for every decoder here:
 *   1. NEVER throw. A `message` listener runs on whatever any frame in the page
 *      posts at it, including deliberate garbage. `null` means "not ours".
 *   2. Never trust a field's type. Everything arrives as `unknown` and is
 *      narrowed before it is believed.
 */

/** Playback state, normalized across both providers. */
export type PlayerState = 'unstarted' | 'playing' | 'paused' | 'buffering' | 'ended' | 'cued';

/** Everything a player can tell us, normalized across both providers. */
export type PlayerEvent =
  | { type: 'ready' }
  | { type: 'state'; state: PlayerState }
  | { type: 'time'; currentTime: number; duration?: number; reportedAt?: number; rate?: number }
  | { type: 'duration'; duration: number }
  | { type: 'rate'; rate: number }
  | { type: 'rates'; rates: number[] }
  | { type: 'seeked'; currentTime: number }
  | { type: 'autoplayBlocked' }
  | { type: 'error'; code?: string | number; message?: string };

/**
 * Everything we can tell a player — one member per `PlaybackAdapter` method
 * that reaches an embed, plus `subscribe` (the provider's own
 * `addEventListener`) and `getDuration` (Vimeo answers it; YouTube reports
 * duration through `infoDelivery` instead).
 *
 * Volume is 0..1 here, as the adapter surface has it. The YouTube encoder is
 * the only place that knows YouTube wants 0-100.
 */
export type PlayerCommand =
  | { kind: 'play' }
  | { kind: 'pause' }
  | { kind: 'seek'; seconds: number; preview?: boolean }
  | { kind: 'setRate'; rate: number }
  | { kind: 'mute' }
  | { kind: 'unmute' }
  | { kind: 'setVolume'; volume01: number }
  | { kind: 'subscribe'; event: string }
  | { kind: 'getDuration' };

// ── Shared narrowing helpers ────────────────────────────────────────────────

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

/** A frame is either a JSON string (YouTube) or a structured clone (Vimeo). */
function parseFrame(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === 'string') {
    try {
      return asRecord(JSON.parse(raw));
    } catch {
      return null;
    }
  }
  return asRecord(raw);
}

/** The value as a finite number, or `null` — `NaN` and `Infinity` are not data. */
function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function positive(value: unknown): number | null {
  const n = finite(value);
  return n !== null && n > 0 ? n : null;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

// ── YouTube ─────────────────────────────────────────────────────────────────

/** `onStateChange.info` — the documented player-state ints. */
const YOUTUBE_STATES: Record<string, PlayerState> = {
  '-1': 'unstarted',
  '0': 'ended',
  '1': 'playing',
  '2': 'paused',
  '3': 'buffering',
  '5': 'cued',
};

/** `onError.info` — the documented error codes, as learner-readable text. */
const YOUTUBE_ERRORS: Record<string, string> = {
  '2': 'This video link is invalid.',
  '5': 'This video cannot be played in the player.',
  '100': 'This video was removed or made private.',
  '101': 'The video owner does not allow it to be embedded.',
  '150': 'The video owner does not allow it to be embedded.',
};

function youTubeCall(cmd: PlayerCommand): { func: string; args: unknown[] } {
  switch (cmd.kind) {
    case 'play':
      return { func: 'playVideo', args: [] };
    case 'pause':
      return { func: 'pauseVideo', args: [] };
    case 'seek':
      // `allowSeekAhead=false` during a scrub keeps YouTube from requesting a
      // new media fragment for every intermediate position; the commit seek
      // (preview absent/false) passes true and actually lands.
      return { func: 'seekTo', args: [cmd.seconds, cmd.preview !== true] };
    case 'setRate':
      return { func: 'setPlaybackRate', args: [cmd.rate] };
    case 'mute':
      return { func: 'mute', args: [] };
    case 'unmute':
      return { func: 'unMute', args: [] };
    case 'setVolume':
      return { func: 'setVolume', args: [Math.round(clamp01(cmd.volume01) * 100)] };
    case 'subscribe':
      return { func: 'addEventListener', args: [cmd.event] };
    case 'getDuration':
      return { func: 'getDuration', args: [] };
  }
}

/**
 * A YouTube widget frame, as the JSON **string** the player expects.
 *
 * Both shapes carry `id` (echoed back on every inbound frame, which is how
 * `decodeYouTube` tells our player from any other iframe in the page) and
 * `channel: 'widget'`.
 */
export function encodeYouTube(cmd: PlayerCommand | { kind: 'listening' }, id: string): string {
  if (cmd.kind === 'listening') {
    return JSON.stringify({ event: 'listening', id, channel: 'widget' });
  }
  const { func, args } = youTubeCall(cmd);
  return JSON.stringify({ event: 'command', func, args, id, channel: 'widget' });
}

/** `infoDelivery` / `initialDelivery` → time (+ what rode along), state, rate, rates. */
function youTubeInfoEvents(info: Record<string, unknown> | null): PlayerEvent[] {
  if (!info) return [];
  const out: PlayerEvent[] = [];
  const currentTime = finite(info.currentTime);
  const duration = positive(info.duration);
  // A UNIX time in SECONDS, not millis — the clock `extrapolateYouTubeTime`
  // measures against.
  const reportedAt = positive(info.currentTimeLastUpdated_);
  const rate = positive(info.playbackRate);

  if (currentTime !== null) {
    out.push({
      type: 'time',
      currentTime,
      ...(duration !== null ? { duration } : {}),
      ...(reportedAt !== null ? { reportedAt } : {}),
      ...(rate !== null ? { rate } : {}),
    });
  } else if (duration !== null) {
    // Duration without a time still moves the scrubber's max.
    out.push({ type: 'duration', duration });
  }

  const playerState = finite(info.playerState);
  const state = playerState === null ? undefined : YOUTUBE_STATES[String(playerState)];
  if (state) out.push({ type: 'state', state });
  if (rate !== null) out.push({ type: 'rate', rate });

  const rates = Array.isArray(info.availablePlaybackRates)
    ? info.availablePlaybackRates.filter((r: unknown): r is number => positive(r) !== null)
    : [];
  if (rates.length > 0) out.push({ type: 'rates', rates });

  return out;
}

/**
 * A YouTube widget frame → normalized events, or `null` when the frame is not
 * ours (wrong `id`, missing `channel`, unparseable, or not an object at all).
 *
 * `[]` is NOT `null`: it means a frame we recognise but have nothing to say
 * about (`onApiChange`, an unmapped state int). The adapter treats any non-null
 * return as proof the handshake landed.
 */
export function decodeYouTube(raw: unknown, id: string): PlayerEvent[] | null {
  try {
    const frame = parseFrame(raw);
    if (!frame) return null;
    if (frame.channel !== 'widget') return null;
    if (frame.id === undefined || frame.id === null) return null;
    if (String(frame.id) !== String(id)) return null;

    switch (typeof frame.event === 'string' ? frame.event : '') {
      case 'initialDelivery':
      case 'infoDelivery':
        return youTubeInfoEvents(asRecord(frame.info));
      case 'onReady':
        return [{ type: 'ready' }];
      case 'onStateChange': {
        // `info` is the int itself; some builds wrap it in `{ playerState }`.
        const code = finite(frame.info) ?? finite(asRecord(frame.info)?.playerState);
        const state = code === null ? undefined : YOUTUBE_STATES[String(code)];
        return state ? [{ type: 'state', state }] : [];
      }
      case 'onPlaybackRateChange': {
        const rate = positive(frame.info);
        return rate === null ? [] : [{ type: 'rate', rate }];
      }
      case 'onError': {
        const numeric = finite(frame.info);
        const code = numeric ?? (typeof frame.info === 'string' ? frame.info : undefined);
        const message = code === undefined ? undefined : YOUTUBE_ERRORS[String(code)];
        return [{ type: 'error', code, message }];
      }
      case 'onAutoplayBlocked':
        return [{ type: 'autoplayBlocked' }];
      case '':
        // No `event` key — a widget-channel frame we have no business reading.
        return null;
      default:
        return [];
    }
  } catch {
    return null;
  }
}

/**
 * YouTube reports `currentTime` on its own undocumented cadence, so a smooth
 * scrubber has to interpolate between frames.
 *
 * `currentTime + min(1, now - reportedAt) * rate`, and only while playing. The
 * one-second clamp is the official API's own: past that, a stalled or
 * backgrounded player would drift ahead of reality with nothing to correct it.
 * Times are SECONDS on both sides (`reportedAt` is `currentTimeLastUpdated_`).
 */
export function extrapolateYouTubeTime(
  snap: { currentTime: number; reportedAt?: number; rate?: number; playing: boolean },
  nowSec: number,
): number {
  const current = finite(snap.currentTime);
  // No usable base is not a reason to invent one: extrapolating from a
  // fabricated 0 produces a fabricated position.
  if (current === null) return 0;
  const base = Math.max(0, current);
  if (!snap.playing) return base;
  const reportedAt = positive(snap.reportedAt);
  const now = finite(nowSec);
  if (reportedAt === null || now === null) return base;
  const elapsed = now - reportedAt;
  // A negative elapsed is clock skew, not time travel.
  if (elapsed <= 0) return base;
  return base + Math.min(1, elapsed) * (positive(snap.rate) ?? 1);
}

// ── Vimeo ───────────────────────────────────────────────────────────────────

/**
 * A Vimeo player.js message, as the plain **object** it structured-clones (not
 * a JSON string — that is the YouTube dialect).
 *
 * `{method}` alone for getters and `ping`; `{method, value}` for everything
 * else, where `addEventListener` takes an ARRAY and every setter takes a bare
 * value.
 */
export function encodeVimeo(cmd: PlayerCommand | { kind: 'ping' }): Record<string, unknown> {
  switch (cmd.kind) {
    case 'ping':
      return { method: 'ping' };
    case 'play':
      return { method: 'play', value: [] };
    case 'pause':
      return { method: 'pause', value: [] };
    case 'seek':
      // Vimeo has no preview seek — every seek lands.
      return { method: 'setCurrentTime', value: cmd.seconds };
    case 'setRate':
      return { method: 'setPlaybackRate', value: cmd.rate };
    case 'mute':
      return { method: 'setMuted', value: true };
    case 'unmute':
      return { method: 'setMuted', value: false };
    case 'setVolume':
      return { method: 'setVolume', value: clamp01(cmd.volume01) };
    case 'subscribe':
      return { method: 'addEventListener', value: [cmd.event] };
    case 'getDuration':
      return { method: 'getDuration' };
  }
}

/** An `{event, data}` frame. */
function vimeoEventEvents(name: string, data: Record<string, unknown> | null): PlayerEvent[] {
  switch (name) {
    case 'ready':
      return [{ type: 'ready' }];
    case 'play':
    case 'playing':
      return [{ type: 'state', state: 'playing' }];
    case 'pause':
      return [{ type: 'state', state: 'paused' }];
    case 'ended':
      return [{ type: 'state', state: 'ended' }];
    case 'bufferstart':
      return [{ type: 'state', state: 'buffering' }];
    case 'bufferend':
      // Deliberately silent: `bufferend` also fires at the end of a preload and
      // after a seek while PAUSED, so the frame alone cannot say what state the
      // player landed in. The next `timeupdate` / `play` / `pause` settles it.
      return [];
    case 'timeupdate': {
      const seconds = finite(data?.seconds);
      if (seconds === null) return [];
      const duration = positive(data?.duration);
      return [{ type: 'time', currentTime: seconds, ...(duration !== null ? { duration } : {}) }];
    }
    case 'seeked': {
      const seconds = finite(data?.seconds);
      return seconds === null ? [] : [{ type: 'seeked', currentTime: seconds }];
    }
    case 'durationchange': {
      const duration = positive(data?.duration);
      return duration === null ? [] : [{ type: 'duration', duration }];
    }
    case 'playbackratechange': {
      const rate = positive(data?.playbackRate);
      return rate === null ? [] : [{ type: 'rate', rate }];
    }
    case 'error': {
      // `data.method` names the call that failed, which is how the player tells
      // "the owner disabled speed control" (`setPlaybackRate`) from a real
      // playback failure. Carry it through as the code.
      const method = typeof data?.method === 'string' ? data.method : undefined;
      const name_ = typeof data?.name === 'string' ? data.name : undefined;
      const message = typeof data?.message === 'string' ? data.message : undefined;
      return [{ type: 'error', code: method ?? name_, message }];
    }
    default:
      return [];
  }
}

/** A `{method, value}` reply to something we asked for. */
function vimeoReplyEvents(method: string, value: unknown): PlayerEvent[] {
  switch (method) {
    case 'ping':
      // The pong IS the ready signal: the player only answers once it is up.
      return [{ type: 'ready' }];
    case 'getDuration': {
      const duration = positive(value);
      return duration === null ? [] : [{ type: 'duration', duration }];
    }
    case 'getPlaybackRate': {
      const rate = positive(value);
      return rate === null ? [] : [{ type: 'rate', rate }];
    }
    case 'getCurrentTime': {
      const currentTime = finite(value);
      return currentTime === null ? [] : [{ type: 'time', currentTime }];
    }
    default:
      // A setter's own acknowledgement — the matching event carries the truth.
      return [];
  }
}

/**
 * A Vimeo player.js frame → normalized events, or `null` when it is not ours.
 *
 * Two inbound shapes: an EVENT (`{event, data}`) we subscribed to, or a METHOD
 * REPLY (`{method, value}`) to something we asked. A JSON string is accepted
 * too — some player builds post strings.
 */
export function decodeVimeo(raw: unknown): PlayerEvent[] | null {
  try {
    const frame = parseFrame(raw);
    if (!frame) return null;
    if (typeof frame.event === 'string') return vimeoEventEvents(frame.event, asRecord(frame.data));
    if (typeof frame.method === 'string') return vimeoReplyEvents(frame.method, frame.value);
    return null;
  } catch {
    return null;
  }
}
